import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { every, first, last, some } from 'lodash-es';

import type { TranslateMissing, TranslateStatus } from './types';
import { isTiKTok } from 'lib/utils/isTikTok';

export interface UseCommentsTranslationControllerArgs {
  comments: Conversation.Trade.Comment[];
  getTranslationFromComment: (comment: Conversation.Trade.Comment) => string | undefined;
  translateMissing?: TranslateMissing;
  translateStatus?: TranslateStatus;
  autoTranslateOnLangChange?: boolean;
  langKey?: string | number;
  itemIndex?: number;
}

export interface CommentsTranslationController {
  needTranslate: boolean;
  translations: Array<string | undefined>;
  displayTranslation: boolean;
  previewText: string;
  translating: boolean;
  translationError: unknown;
  toggleTranslation: () => void;
}

export function useCommentsTranslationController(
  args: UseCommentsTranslationControllerArgs,
): CommentsTranslationController {
  const {
    comments,
    getTranslationFromComment,
    translateMissing,
    translateStatus,
    autoTranslateOnLangChange = true,
    langKey,
    itemIndex,
  } = args;

  const needTranslate = useMemo(
    () => some(comments, (comment) => Boolean(comment.buyerComment)),
    [comments],
  );

  const translations = useMemo(
    () => comments.map((comment) => getTranslationFromComment(comment)),
    [comments, getTranslationFromComment],
  );

  const allTranslated = useMemo(
    () => every(translations, (translation) => typeof translation === 'string') && needTranslate,
    [translations, needTranslate],
  );

  const [manualDisplayTranslation, setManualDisplayTranslation] = useState<boolean | null>(null);
  const displayTranslation = useMemo(() => {
    if (!needTranslate) return false;
    if (typeof manualDisplayTranslation === 'boolean') return manualDisplayTranslation;
    return allTranslated;
  }, [allTranslated, manualDisplayTranslation, needTranslate]);

  const translating = Boolean(translateStatus?.pending);
  const translationError = translateStatus?.error;
  const translatingMissingRef = useRef<Promise<void> | null>(null);

  const ensureMissingTranslations = useCallback(async () => {
    if (!translateMissing) return;
    if (translatingMissingRef.current) return translatingMissingRef.current;

    const task = Promise.all(
      comments.map((comment, commentIndex) => {
        if (comment.buyerComment === '') return Promise.resolve();
        if (typeof getTranslationFromComment(comment) === 'string') return Promise.resolve();

        return translateMissing({ comment, commentIndex, itemIndex });
      }),
    )
      .then(() => undefined)
      .finally(() => {
        translatingMissingRef.current = null;
      });

    translatingMissingRef.current = task;
    await task;
  }, [comments, getTranslationFromComment, translateMissing, itemIndex]);

  const toggleTranslation = useCallback(() => {
    if (!needTranslate) return;

    if (displayTranslation) {
      setManualDisplayTranslation(false);
      return;
    }

    if (allTranslated) {
      setManualDisplayTranslation(true);
      return;
    }

    ensureMissingTranslations()
      .then(() => {
        setManualDisplayTranslation(true);
      })
      .catch(() => {
        // 错误态由 translateStatus 负责呈现
      });
  }, [allTranslated, displayTranslation, ensureMissingTranslations, needTranslate]);

  const previewText = useMemo(() => {
    const lastComment = last(comments);
    const lastTranslation = last(translations);
    if (!lastComment) return '';

    return displayTranslation
      ? lastTranslation || lastComment.buyerComment
      : lastComment.buyerComment;
  }, [comments, displayTranslation, translations]);

  useEffect(() => {
    if (!needTranslate) {
      setManualDisplayTranslation(null);
      return;
    }

    if (!autoTranslateOnLangChange) return;
    setManualDisplayTranslation(null);

    if (allTranslated || !translateMissing) return;

    let disposed = false;
    ensureMissingTranslations()
      .then(() => {
        if (disposed) return;
        setManualDisplayTranslation(true);
      })
      .catch(() => {
        // 错误态由 translateStatus 负责呈现
      });

    return () => {
      disposed = true;
    };
  }, [
    allTranslated,
    autoTranslateOnLangChange,
    ensureMissingTranslations,
    langKey,
    needTranslate,
    translateMissing,
  ]);

  return {
    needTranslate,
    translations,
    displayTranslation,
    previewText,
    translating,
    translationError,
    toggleTranslation,
  };
}

export function useCommentRatingValue(comments: Conversation.Trade.Comment[], platform: string) {
  const ratingValue = useMemo(() => {
    if (comments.length === 0) return 0;
    return isTiKTok(platform)
      ? first(comments)!.rawRatings.ratingStar
      : last(comments)!.rawRatings.ratingStar;
  }, [comments, platform]);

  return {
    ratingValue,
  };
}
